import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, forkJoin, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';

import { PreApplicationService } from '../../../../core/applications/pre-application.service';
import {
  PreApplication,
  PreApplicationRecommendationResult,
  PRE_DOC_TYPE_LABELS,
} from '../../../../core/applications/pre-application.types';
import { IconComponent } from '../../../../shared/icon/icon';
import { StatusChipComponent } from '../../../../shared/status-chip/status-chip';
import { DashPipe } from '../../../../shared/pipes/dash.pipe';
import { DocVerdictComponent } from '../../shared/doc-verdict/doc-verdict';
import { DeclarationsPanelComponent } from '../../../../shared/declarations-panel/declarations-panel';
import { ContextBlocksClientComponent } from '../../shared/context-blocks-client/context-blocks-client';

/**
 * A read-only view of a submitted pre-application (FLOW-223).
 *
 * 🚨 This screen exists because the dashboard's "View submission" CTA had nowhere
 * to go. It pointed at the guided intake, which deliberately refuses to hydrate a
 * SUBMITTED or PROCEEDED record (`product-chooser.ts` — hydrating one would lock
 * the wizard on the "under review" panel with no way to start again). Both
 * decisions were right; what was missing was somewhere to look. So this is that
 * third thing, and it must NOT grow edit affordances — no upload control, no
 * resubmit button, not even a disabled one. A client who needs to change
 * something goes back through the wizard, which is what MORE_INFO_REQUIRED and
 * DECLINED still route to.
 *
 * ⚠️ The declared lists are the ONE exception to the paragraph above, and it is
 * not a softening of it. Funders, related parties and directors are facts about
 * the ENTITY, not fields of this pre-application — declaring one changes nothing
 * about the submitted record and cannot re-open it. They were previously only
 * capturable on the Kickstart client detail, which does not exist until the
 * pre-application PROCEEDS, so the client was asked for them at the one moment
 * they could not give them (FLOW-281). Client mode only: the accounts section is
 * gated to Ops because the client's accounts surface is the wizard's bank step.
 *
 * N4: everything rendered here comes from the client serializer or the client
 * recommendation endpoint. The raw routing enum (`recommendation`) rides along on
 * the recommendation response but is internal — only `headline`, `reason` and
 * `declined` are shown. Never reach for the Ops endpoints from this component.
 */
@Component({
  selector: 'app-pre-application-view',
  imports: [
    MatCardModule,
    MatButtonModule,
    RouterLink,
    DatePipe,
    IconComponent,
    StatusChipComponent,
    DashPipe,
    DocVerdictComponent,
    DeclarationsPanelComponent,
    ContextBlocksClientComponent,
  ],
  templateUrl: './pre-application-view.html',
  styleUrl: './pre-application-view.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreApplicationViewComponent implements OnInit {
  private readonly preAppService = inject(PreApplicationService);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly entityId = this.route.snapshot.paramMap.get('id') ?? '';
  private readonly preAppId = this.route.snapshot.paramMap.get('preAppId') ?? '';

  protected readonly DOC_TYPE_LABELS = PRE_DOC_TYPE_LABELS;

  protected readonly preApp = signal<PreApplication | null>(null);
  /** Null when the outcome copy couldn't be fetched — the page still renders. */
  protected readonly outcome = signal<PreApplicationRecommendationResult | null>(null);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);
  /**
   * The record is not this user's to see — 403 OR 404.
   *
   * 🚨 Both statuses land here on purpose. The API scopes by entity and returns
   * 404 for a pre-application outside the caller's entities
   * (`applications/views.py:173`), so "does not exist" and "exists but is not
   * yours" are deliberately indistinguishable. Splitting them in the UI would
   * hand that distinction back to anyone probing short_ids, which is the whole
   * thing the backend is refusing to disclose.
   */
  protected readonly denied = signal(false);

  protected readonly documents = computed(() => this.preApp()?.documents ?? []);

  /**
   * Whether the declared lists are closed on this screen (FLOW-281).
   *
   * SUBMITTED and MORE_INFO_REQUIRED are exactly the states in which the review
   * is still running and the facts are still wanted, so both stay editable.
   * PROCEEDED hands the register to the Kickstart client detail, which is the
   * live surface from that point; DECLINED has no review left to inform. Keyed
   * on the two closed states rather than on "is it SUBMITTED", so a new status
   * added to `ReviewStatus` (`applications/models.py:55`) arrives editable and
   * visible rather than silently locked.
   */
  protected readonly declarationsReadOnly = computed(() => {
    const status = this.preApp()?.review_status;
    return status === 'PROCEEDED' || status === 'DECLINED';
  });

  ngOnInit(): void {
    this.load();
  }

  protected load(): void {
    this.isLoading.set(true);
    this.loadError.set(false);
    this.denied.set(false);

    // The outcome copy is supporting detail, not the record: a failure there
    // should not cost the client the submission they came to look at.
    forkJoin({
      preApp: this.preAppService.get(this.preAppId),
      outcome: this.preAppService
        .recommendation(this.preAppId)
        .pipe(catchError(() => of(null))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ preApp, outcome }) => {
          this.preApp.set(preApp);
          this.outcome.set(outcome);
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          // 404 is the scoped-lookup miss, not a broken page: treat it exactly
          // as 403. Anything else is genuinely our fault and offers a retry.
          if (err.status === 403 || err.status === 404) {
            this.denied.set(true);
          } else {
            this.loadError.set(true);
          }
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
      });
  }
}
